// Per-column filters. Renders a single row of inputs above the table body.
// Each input dispatches state.setFilter(key, value) on change.

import type { ChangeEvent, JSX } from "react";
import type { ColumnDef, DataTableState, FilterPrimitive } from "./types.js";

const INPUT_CLS = "w-full text-sm rounded-lg border border-slate-300 bg-white px-2 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500";
const CLEAR_BTN = "text-xs text-navy-800 hover:text-navy-950 underline focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 rounded";

interface Props<T> {
  columns: ReadonlyArray<ColumnDef<T>>;
  state: DataTableState;
}

function readCurrent(state: DataTableState, key: string): string {
  const v = state.query[key];
  if (v == null) return "";
  return String(v);
}

function onChangeValue(state: DataTableState, key: string, raw: string): void {
  if (raw === "") state.setFilter(key, undefined);
  else state.setFilter(key, raw as FilterPrimitive);
}

function TextFilter({ col, state }: { col: ColumnDef<unknown>; state: DataTableState }): JSX.Element {
  const key = String(col.accessorKey);
  return (
    <input type="text" className={INPUT_CLS} aria-label={`Filter by ${col.header}`}
      placeholder={`Filter ${col.header.toLowerCase()}`} value={readCurrent(state, key)}
      onChange={(e: ChangeEvent<HTMLInputElement>) => onChangeValue(state, key, e.target.value)} />
  );
}

function DateFilter({ col, state }: { col: ColumnDef<unknown>; state: DataTableState }): JSX.Element {
  const key = String(col.accessorKey);
  return (
    <input type="date" className={INPUT_CLS} aria-label={`Filter by ${col.header}`}
      value={readCurrent(state, key)}
      onChange={(e: ChangeEvent<HTMLInputElement>) => onChangeValue(state, key, e.target.value)} />
  );
}

function SelectFilter({ col, state }: { col: ColumnDef<unknown>; state: DataTableState }): JSX.Element {
  const key = String(col.accessorKey);
  return (
    <select className={INPUT_CLS} aria-label={`Filter by ${col.header}`}
      value={readCurrent(state, key)}
      onChange={(e: ChangeEvent<HTMLSelectElement>) => onChangeValue(state, key, e.target.value)}>
      <option value="">All</option>
      {(col.filterOptions ?? []).map((opt) => (
        <option key={String(opt.value)} value={String(opt.value)}>{opt.label}</option>
      ))}
    </select>
  );
}

function FilterCell({ col, state }: { col: ColumnDef<unknown>; state: DataTableState }): JSX.Element {
  if (!col.filterable) return <td className="py-2 pr-4" />;
  const t = col.filterType ?? "text";
  if (t === "date") return <td className="py-2 pr-4"><DateFilter col={col} state={state} /></td>;
  if (t === "select") return <td className="py-2 pr-4"><SelectFilter col={col} state={state} /></td>;
  return <td className="py-2 pr-4"><TextFilter col={col} state={state} /></td>;
}

function hasActiveFilters(state: DataTableState, columns: ReadonlyArray<ColumnDef<unknown>>): boolean {
  return columns.some((c) => c.filterable && state.query[String(c.accessorKey)] != null);
}

export function DataTableFilters<T>({ columns, state }: Props<T>): JSX.Element | null {
  const visible = columns.filter((c) => !c.hidden);
  const anyFilterable = visible.some((c) => c.filterable);
  if (!anyFilterable) return null;
  const active = hasActiveFilters(state, visible as ReadonlyArray<ColumnDef<unknown>>);
  return (
    <tr className="border-b border-slate-100 bg-slate-50/40">
      {visible.map((c) => (
        <FilterCell key={`f-${String(c.accessorKey)}`} col={c as ColumnDef<unknown>} state={state} />
      ))}
      {active && (
        <td className="py-2 pr-4 text-right">
          <button type="button" className={CLEAR_BTN} onClick={state.clearFilters}>
            Clear filters
          </button>
        </td>
      )}
    </tr>
  );
}
